import React, { useState } from 'react';
import { Facebook, Instagram, X, Linkedin, Youtube, CheckCircle, AlertCircle, Link, Unlink, Shield, Crown } from 'lucide-react';
import { useSocialConnections } from '../../hooks/useSocialConnections';
import { motion, AnimatePresence } from 'framer-motion';
import InstagramRequirementsModal from '../modals/InstagramRequirementsModal';
import { useConnectedAccountsLimit } from '../../hooks/useSocialMediaLimits';
import { UpgradeModal } from '../modals/UpgradeModal';

const platforms = [
    { 
        name: 'Facebook', 
        provider: 'facebook', 
        icon: Facebook, 
        color: 'bg-blue-600 hover:bg-blue-700',
        description: 'Connect to manage Facebook pages and posts'
    },
    { 
        name: 'Instagram', 
        provider: 'instagram', 
        icon: Instagram, 
        color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
        description: 'Connect to share photos and stories'
    },
    { 
        name: 'X', 
        provider: 'twitter', 
        icon: X, 
        color: 'bg-black hover:bg-gray-900 dark:bg-white dark:hover:bg-gray-100',
        description: 'Connect to post and engage on X (formerly Twitter)'
    },
    { 
        name: 'LinkedIn', 
        provider: 'linkedin', 
        icon: Linkedin, 
        color: 'bg-blue-700 hover:bg-blue-800',
        description: 'Connect for professional networking'
    },
    { 
        name: 'YouTube', 
        provider: 'youtube', 
        icon: Youtube, 
        color: 'bg-orange-500 hover:bg-orange-600',
        description: 'Connect to manage video content'
    },
];

const SocialConnect: React.FC = () => {
    const { 
        connections, 
        handleConnect, 
        handleDisconnect, 
        isConnected,
        showInstagramModal,
        setShowInstagramModal,
        proceedWithInstagramConnection
    } = useSocialConnections();
    const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    
    // Get connected accounts limit
    const accountsLimit = useConnectedAccountsLimit();
    const isConnectionLocked = !accountsLimit.isLoading && accountsLimit.limit === 0;

    const handleDisconnectWithConfirm = async (provider: string) => {
        setDisconnectingPlatform(provider);
    };

    const confirmDisconnect = async (provider: string) => {
        await handleDisconnect(provider);
        setDisconnectingPlatform(null);
    };

    return (
        <div className="bg-white dark:bg-slate-900/70 rounded-2xl p-6 border border-gray-200/80 dark:border-slate-800 shadow-sm">
            <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Link className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Social Media Accounts</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Connect your social media accounts to manage posts and analytics</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platforms.map((platform) => {
                    const Icon = platform.icon;
                    const connected = isConnected(platform.provider);
                    
                    return (
                        <motion.div 
                            key={platform.provider} 
                            className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-800/50 hover:shadow-lg transition-all duration-300"
                            whileHover={{ scale: 1.02 }}
                        >
                            <div className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connected ? platform.color : 'bg-gray-200 dark:bg-gray-700'}`}>
                                            <Icon className={`w-5 h-5 ${
                                                connected 
                                                    ? platform.provider === 'twitter' 
                                                        ? 'text-black dark:text-white' 
                                                        : 'text-white'
                                                    : 'text-gray-500 dark:text-gray-400'
                                            }`} />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{platform.name}</h3>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">{platform.description}</p>
                                        </div>
                                    </div>
                                    
                                    {connected ? (
                                        <div className="flex items-center space-x-1 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                                            <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                                            <span className="text-xs font-medium text-green-700 dark:text-green-300">Connected</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
                                            <AlertCircle className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Not Connected</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex space-x-2">
                                    {connected ? (
                                        <>
                                            <button
                                                onClick={() => handleDisconnectWithConfirm(platform.provider)}
                                                className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                                            >
                                                <Unlink className="w-4 h-4" />
                                                <span>Disconnect</span>
                                            </button>
                                            <button className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm">
                                                Settings
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => isConnectionLocked ? setShowUpgradeModal(true) : handleConnect(platform.provider)}
                                            className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-white rounded-lg transition-all duration-200 text-sm font-medium ${platform.color}`}
                                        >
                                            {isConnectionLocked && <Crown className="w-4 h-4 text-yellow-300" />}
                                            {!isConnectionLocked && <Link className="w-4 h-4" />}
                                            <span>Connect {platform.name}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
                <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">Secure Connection</h4>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                            Your social media accounts are connected securely using OAuth 2.0. We never store your passwords and you can disconnect at any time.
                        </p>
                    </div>
                </div>
            </div>

            {/* Instagram Requirements Modal */}
            <InstagramRequirementsModal
                isOpen={showInstagramModal}
                onClose={() => setShowInstagramModal(false)}
                onProceed={proceedWithInstagramConnection}
            />
            
            {/* Upgrade Modal */}
            <UpgradeModal
                open={showUpgradeModal}
                onOpenChange={setShowUpgradeModal}
                title="Upgrade to Connect Social Accounts"
                message="Connecting social media accounts is available on paid plans. Upgrade now to connect your platforms and start publishing."
                feature="social-connections"
            />

            {/* Disconnect Confirmation Modal */}
            <AnimatePresence>
                {disconnectingPlatform && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setDisconnectingPlatform(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Unlink className="w-6 h-6 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                    Disconnect {platforms.find(p => p.provider === disconnectingPlatform)?.name}?
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                                    You won't be able to post to this platform or access its analytics until you reconnect.
                                </p>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => setDisconnectingPlatform(null)}
                                        className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => confirmDisconnect(disconnectingPlatform)}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        Disconnect
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SocialConnect;
